<!-- ThemeEditorFeatureSelectorModal.vue -->

<template>
  <CreatorPlanLimitPrompt :open="showLimitPrompt" :resource="limitResource" @close="showLimitPrompt = false" />
  <div class="feature-selector-shell relative grid max-h-[calc(100vh-7rem)] min-h-[620px] overflow-y-auto rounded-[28px] border border-slate-200/80 bg-[#f8faff] lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,.85fr)] lg:overflow-hidden">
    <div class="pointer-events-none absolute -left-20 -top-24 h-64 w-64 rounded-full bg-blue-200/35 blur-3xl" />
    <div class="pointer-events-none absolute -bottom-28 right-8 h-72 w-72 rounded-full bg-violet-200/30 blur-3xl" />

    <aside class="relative z-10 flex min-w-0 flex-col p-5 sm:p-7 lg:overflow-y-auto lg:p-9">
      <div class="max-w-2xl">
        <span class="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white/80 px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.15em] text-[#176fe2] shadow-sm">
          <SparklesIcon class="h-4 w-4" /> Product features
        </span>
        <h2 class="mt-4 text-2xl font-extrabold tracking-tight text-slate-950 sm:text-3xl">
          What would you like to add?
        </h2>
        <p class="mt-2 text-sm leading-6 text-slate-500">
          Choose a feature for your member area. Select a card to see what members will experience.
        </p>
      </div>

      <div v-if="fetchError" class="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
        {{ fetchError }}
      </div>

      <div v-if="isFetchingFeatures" class="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div v-for="index in 9" :key="index" class="h-[124px] animate-pulse rounded-2xl border border-white bg-white/70 shadow-sm" />
      </div>

      <div v-else class="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <button
          v-for="feature in features"
          :key="feature.feature_id"
          type="button"
          class="feature-option group relative flex min-h-[124px] flex-col items-start justify-between overflow-hidden rounded-2xl border p-4 text-left transition duration-200"
          :class="selectedFeature?.feature_id === feature.feature_id ? 'feature-option-selected' : 'border-slate-200 bg-white/80 hover:-translate-y-0.5 hover:border-blue-200 hover:bg-white hover:shadow-lg hover:shadow-blue-900/5'"
          :aria-pressed="selectedFeature?.feature_id === feature.feature_id"
          @click="selectFeature(feature)"
        >
          <span class="flex w-full items-start justify-between gap-2">
            <span class="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 transition group-hover:bg-blue-50 group-hover:text-[#2D8CFF]" :class="selectedFeature?.feature_id === feature.feature_id ? '!bg-[#2D8CFF] !text-white shadow-lg shadow-blue-500/20' : ''">
              <component :is="feature.icon" class="h-5 w-5" />
            </span>
            <span v-if="selectedFeature?.feature_id === feature.feature_id" class="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-white shadow-sm">
              <CheckIcon class="h-4 w-4" />
            </span>
          </span>
          <span class="mt-4 block text-sm font-bold leading-5 text-slate-800">
            {{ featureDisplayName(feature) }}
          </span>
        </button>
      </div>

      <div class="mt-auto flex flex-col gap-3 pt-7 sm:flex-row sm:items-center sm:justify-between">
        <p class="text-xs leading-5 text-slate-500">
          You can customize the name, access, and design after adding it.
        </p>
        <button
          type="button"
          class="inline-flex min-h-[46px] shrink-0 items-center justify-center gap-2 rounded-xl bg-[#2D8CFF] px-5 py-3 text-sm font-bold text-white shadow-lg shadow-blue-500/20 transition hover:-translate-y-0.5 hover:bg-[#176fe2] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
          :disabled="!selectedFeature || isLoading"
          @click="addFeatureToCreator"
        >
          <span v-if="isLoading" class="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
          <PlusIcon v-else class="h-4 w-4" />
          {{ isLoading ? 'Adding feature…' : 'Add to member area' }}
        </button>
      </div>
    </aside>

    <main class="relative z-10 border-t border-slate-200/80 bg-white/55 p-5 sm:p-7 lg:overflow-y-auto lg:border-l lg:border-t-0 lg:p-9">
      <div v-if="selectedFeature" class="preview-card mx-auto flex h-full max-w-xl flex-col overflow-hidden rounded-[24px] border border-white/90 bg-white/80 shadow-[0_24px_70px_rgba(43,72,120,.12)] backdrop-blur-xl">
        <div class="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-violet-100">
          <img
            v-if="selectedFeature.image"
            :src="selectedFeature.image"
            :alt="`${featureDisplayName(selectedFeature)} preview`"
            class="h-full w-full object-cover object-top"
          >
          <div v-else class="flex h-full items-center justify-center text-[#2D8CFF]">
            <component :is="selectedFeature.icon" class="h-16 w-16" />
          </div>
          <span class="absolute left-4 top-4 rounded-full border border-white/80 bg-white/90 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#176fe2] shadow-sm backdrop-blur">
            Feature preview
          </span>
        </div>

        <div class="flex flex-1 flex-col p-6 sm:p-7">
          <div class="flex items-start gap-4">
            <span class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-[#2D8CFF]">
              <component :is="selectedFeature.icon" class="h-6 w-6" />
            </span>
            <div class="min-w-0">
              <p class="text-[10px] font-extrabold uppercase tracking-[0.16em] text-slate-400">Selected feature</p>
              <h3 class="mt-1 text-xl font-extrabold tracking-tight text-slate-950">
                {{ featureDisplayName(selectedFeature) }}
              </h3>
            </div>
          </div>
          <p class="mt-5 text-sm leading-7 text-slate-600">
            {{ featureDescription(selectedFeature) }}
          </p>
          <div class="mt-auto pt-7">
            <div class="rounded-2xl border border-blue-100 bg-blue-50/70 p-4">
              <p class="text-sm font-bold text-slate-900">Ready to make it yours?</p>
              <p class="mt-1 text-xs leading-5 text-slate-500">Add this feature now, then configure its content and member access from the Theme Editor.</p>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="flex h-full min-h-[420px] items-center justify-center rounded-[24px] border border-dashed border-slate-300 bg-white/60 p-8 text-center">
        <div>
          <span class="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-[#2D8CFF]"><SparklesIcon class="h-7 w-7" /></span>
          <h3 class="mt-4 text-lg font-bold text-slate-900">Select a feature</h3>
          <p class="mt-2 max-w-xs text-sm leading-6 text-slate-500">Choose a card to preview its member experience here.</p>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useFeatureStore } from '@/stores/featureStore';
import CreatorPlanLimitPrompt from '@/Components/CreatorPlanLimitPrompt.vue';
import { CheckIcon, PlusIcon, SparklesIcon } from '@heroicons/vue/24/outline';

import { useRouter } from 'vue-router'

import onlineCourseImage from '@/assets/media/images/online-course.png';
import videoChannelImage from '@/assets/media/images/video-channel-platforma-select.png';
import quizzesImage from '@/assets/media/images/quiz.png';
// import calculatorImage from '@/assets/media/images/online-course.png';
import groupnewsfeedImage from '@/assets/media/images/newsfeed.png';
import shopifyImage from '@/assets/media/images/shopify.png';
import chatImage from '@/assets/media/images/chat.png';
import webinarImage from '@/assets/media/images/webinar.png';
import meetImage from '@/assets/media/images/meet.webp';
import podcastImage from '@/assets/media/images/podcast.webp';
import articlesImage from '@/assets/media/images/articles.webp';
import journalImage from '@/assets/media/images/journal.webp';
import flixTheatreImage from '@/assets/media/images/flix-theatre.webp';

const router = useRouter()

const emit = defineEmits(["close", "new-feature"])



// Access feature store function to refresh feature instances after adding a feature
const { fetchFeatureInstances } = useFeatureStore();

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;

// State
const features = ref([]);
const selectedFeature = ref(null);
const isLoading = ref(false)
const isFetchingFeatures = ref(true)
const fetchError = ref('')
const showLimitPrompt = ref(false)
const limitResource = ref(null)

const imageMap = {
  Chat: chatImage,
  Course: onlineCourseImage,
  "Video Channel": videoChannelImage,
  "Flix Theatre": flixTheatreImage,
  Journal: journalImage,
  Webinar: webinarImage,
  "Shopify Storefront": shopifyImage,
  "Storefront": shopifyImage,
  Newsfeed: groupnewsfeedImage,
  Quiz: quizzesImage,
  Meet: meetImage,
  Podcast: podcastImage,
  Articles: articlesImage,
};

// Fetch feature templates from the backend on mount
const fetchFeatures = async () => {
  isFetchingFeatures.value = true;
  fetchError.value = '';
  try {
    const response = await fetch(`${apiProxyUrl}/api/feature-templates`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      features.value = data.templates.map((feature) => ({
        ...feature,
        icon: resolveIcon(feature.icon), // Resolve the icon dynamically
        image: imageMap[feature.name] || null,
      }));
      
      // ⭐ DEFAULT SELECT: CHAT
      const chat = features.value.find(f => f.name === "Chat");
      if (chat) selectedFeature.value = chat;

    } else {
      fetchError.value = 'Features could not be loaded. Please close this window and try again.';
      console.error('Failed to fetch feature templates:', response.statusText);
    }
  } catch (error) {
    fetchError.value = 'Features could not be loaded. Please close this window and try again.';
    console.error('Error fetching feature templates:', error);
  } finally {
    isFetchingFeatures.value = false;
  }
};

// Add selected feature instance to creator's features
const addFeatureToCreator = async () => {
  if (!selectedFeature.value) return;

  try {
    isLoading.value = true;
    const response = await fetch(`${apiProxyUrl}/api/feature-instances`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ featureId: selectedFeature.value.feature_id }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData?.code === 'creator_plan_limit_reached') {
        limitResource.value = {
          key: errorData?.details?.limitKey || 'product_features',
          label: errorData?.details?.label || `${featureDisplayName(selectedFeature.value) || 'Product'} features`,
          usage: errorData?.details?.usage,
          publicLimit: errorData?.details?.limit,
          state: errorData?.details?.state || 'blocked',
        };
        showLimitPrompt.value = true;
      } else {
        console.error('Failed to add feature:', errorData?.message || response.statusText);
      }
      return;
    }

    const data = await response.json(); // <= includes the new slug

    await fetchFeatureInstances();

    emit('new-feature', data.slug);

    emit('close'); // close modal
    
    router.push(`/creator/theme-editor/${data.slug}`); // navigate to new instance
    

  } catch (err) {
    console.error('Error adding feature:', err);
  } finally {
    isLoading.value = false;
  }
};

// Select a feature for preview
const selectFeature = (feature) => {
  selectedFeature.value = feature;
};

const featureDisplayName = (feature) => {
  const name = String(feature?.name || '').trim();
  return name === 'Shopify Storefront' ? 'Storefront' : name;
};

const featureDescription = (feature) => {
  const name = String(feature?.name || '').trim();
  if (name === 'Shopify Storefront' || name === 'Storefront') {
    return 'Create a polished, Shopify-like shopping experience inside your member app, with products, checkout, orders, shipping, and fulfillment managed through Platforma.';
  }
  return feature?.description || '';
};

// Resolve icon from icon name

const resolveIcon = (iconName) => {
  const icons = {
    ChatBubbleBottomCenterTextIcon: require("@heroicons/vue/24/outline").ChatBubbleBottomCenterTextIcon,
    AcademicCapIcon: require("@heroicons/vue/24/outline").AcademicCapIcon,
    VideoCameraIcon: require("@heroicons/vue/24/outline").VideoCameraIcon,
    BookOpenIcon: require("@heroicons/vue/24/outline").BookOpenIcon,
    GlobeAltIcon: require("@heroicons/vue/24/outline").GlobeAltIcon,
    ShoppingCartIcon: require("@heroicons/vue/24/outline").ShoppingCartIcon,
    NewspaperIcon: require("@heroicons/vue/24/outline").NewspaperIcon,
    FilmIcon: require("@heroicons/vue/24/outline").FilmIcon,
    QueueListIcon: require("@heroicons/vue/24/outline").QueueListIcon,
    PhoneArrowUpRightIcon: require("@heroicons/vue/24/outline").PhoneArrowUpRightIcon,
    MicrophoneIcon: require("@heroicons/vue/24/outline").MicrophoneIcon,
    DocumentTextIcon: require("@heroicons/vue/24/outline").DocumentTextIcon,
    CalendarDaysIcon: require("@heroicons/vue/24/outline").PhoneArrowUpRightIcon,
  };

  return icons[iconName] || null;
};

// Fetch features when component mounts
onMounted(fetchFeatures);
</script>

  
<style scoped>
.feature-selector-shell {
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .9);
}

.feature-option {
  box-shadow: 0 1px 2px rgba(15, 23, 42, .03);
}

.feature-option-selected {
  border-color: rgba(45, 140, 255, .75);
  background: linear-gradient(145deg, rgba(239, 246, 255, .98), rgba(255, 255, 255, .96));
  box-shadow: 0 0 0 3px rgba(45, 140, 255, .09), 0 14px 32px rgba(45, 140, 255, .1);
}

.feature-selector-shell,
.feature-selector-shell aside,
.feature-selector-shell main {
  scrollbar-color: rgba(148, 163, 184, .58) transparent;
  scrollbar-width: thin;
}

.feature-selector-shell::-webkit-scrollbar,
.feature-selector-shell aside::-webkit-scrollbar,
.feature-selector-shell main::-webkit-scrollbar {
  height: 6px;
  width: 6px;
}

.feature-selector-shell::-webkit-scrollbar-thumb,
.feature-selector-shell aside::-webkit-scrollbar-thumb,
.feature-selector-shell main::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(148, 163, 184, .58);
}
</style>
  
